import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  CreatePlayerPayload,
  ImageUpload,
  Player,
  PlayerDTO,
  SmsOptInPayload,
  UpdateImagePayload,
  UpdateMyPlayerPayload,
  UpdatePlayerPayload,
  UpdatePlayersPayload,
  createPlayerPayloadSchema,
  playerSchema,
  smsOptInPayloadSchema,
  updateMyPlayerPayloadSchema,
  updatePlayerPayloadSchema,
  updatePlayersPayloadSchema,
} from '@optical/types';

@Injectable({ providedIn: 'root' })
export class PlayerService {
  private readonly http = inject(HttpClient);

  async createPlayer(payload: CreatePlayerPayload): Promise<Player> {
    const dto = createPlayerPayloadSchema.encode(payload);
    const response = await firstValueFrom(this.http.post<PlayerDTO>('/players', dto));

    return playerSchema.decode(response);
  }

  async createPlayerOfTeam(payload: CreatePlayerPayload, teamId: string): Promise<Player> {
    const dto = createPlayerPayloadSchema.encode(payload);
    const response = await firstValueFrom(
      this.http.post<PlayerDTO>(`/teams/${teamId}/players`, dto),
    );

    return playerSchema.decode(response);
  }

  async deletePlayer(playerId: string): Promise<void> {
    await firstValueFrom(this.http.delete<void>(`/players/${playerId}`));
  }

  async deletePlayerImage(playerId: string): Promise<void> {
    await firstValueFrom(this.http.delete<void>(`/players/${playerId}/image`));
  }

  /**
   * Resolves the roster player the signed-in player user is linked to.
   *
   * Player users never learn their own player id — the server keeps the mapping — so this is the
   * player experience's entry point into roster data.
   */
  async getMyPlayer(): Promise<Player> {
    const response = await firstValueFrom(this.http.get<PlayerDTO>('/players/me'));

    return playerSchema.decode(response);
  }

  /**
   * Applies the signed-in player user's own edits to their roster entry.
   *
   * Narrower than {@link updatePlayer} by design: the payload only carries the attributes a player
   * owns, and the server binds the same shape at the route.
   */
  async updateMyPlayer(payload: UpdateMyPlayerPayload): Promise<Player> {
    const dto = updateMyPlayerPayloadSchema.encode(payload);
    const response = await firstValueFrom(this.http.put<PlayerDTO>('/players/me', dto));

    return playerSchema.decode(response);
  }

  /**
   * Starts an avatar upload for the signed-in player user's own roster entry.
   *
   * The player counterpart of {@link updatePlayerImage} and the same two-step flow: the returned
   * `imageUploadUrl` takes the file, `imageUrl` is where it will be readable from afterwards.
   */
  async updateMyPlayerImage(payload: UpdateImagePayload): Promise<ImageUpload> {
    return await firstValueFrom(this.http.put<ImageUpload>('/players/me/image', payload));
  }

  /** Removes the avatar from the signed-in player user's own roster entry. */
  async deleteMyPlayerImage(): Promise<void> {
    await firstValueFrom(this.http.delete<void>('/players/me/image'));
  }

  /**
   * Records the signed-in player user's consent to receive text messages at `payload.phone`.
   *
   * Not part of {@link updateMyPlayer}: the server has to take the number off the carrier opt-out
   * list and confirm the opt-in with a message, and the consent it stores is only valid for the
   * number it was given for. The player that comes back carries the state to render — including
   * the case where the number turns out to have opted out after all.
   */
  async optInToMySmsNotifications(payload: SmsOptInPayload): Promise<Player> {
    const dto = smsOptInPayloadSchema.encode(payload);
    const response = await firstValueFrom(
      this.http.put<PlayerDTO>('/players/me/sms-notifications', dto),
    );

    return playerSchema.decode(response);
  }

  /** Withdraws the signed-in player user's consent to receive text messages. */
  async optOutOfMySmsNotifications(): Promise<Player> {
    const response = await firstValueFrom(
      this.http.delete<PlayerDTO>('/players/me/sms-notifications'),
    );

    return playerSchema.decode(response);
  }

  async getPlayers(): Promise<Player[]> {
    const response = await firstValueFrom(this.http.get<PlayerDTO[]>('/players'));

    return response.map((playerDto) => playerSchema.decode(playerDto));
  }

  async getPlayersOfTeam(teamId: string): Promise<Player[]> {
    const response = await firstValueFrom(this.http.get<PlayerDTO[]>(`/teams/${teamId}/players`));

    return response.map((playerDto) => playerSchema.decode(playerDto));
  }

  async updatePlayer(playerId: string, payload: UpdatePlayerPayload): Promise<Player> {
    const dto = updatePlayerPayloadSchema.encode(payload);
    const response = await firstValueFrom(this.http.put<PlayerDTO>(`/players/${playerId}`, dto));

    return playerSchema.decode(response);
  }

  async assignAprilTag(playerId: string): Promise<Player> {
    const response = await firstValueFrom(
      this.http.put<PlayerDTO>(`/players/${playerId}/april-tag`, {}),
    );

    return playerSchema.decode(response);
  }

  async downloadAprilTag(playerId: string): Promise<Blob> {
    return await firstValueFrom(
      this.http.get(`/players/${playerId}/april-tag`, {
        responseType: 'blob',
      }),
    );
  }

  /** The player analog of {@link assignAprilTag}, for the signed-in player user's own entry. */
  async assignMyAprilTag(): Promise<Player> {
    const response = await firstValueFrom(this.http.put<PlayerDTO>('/players/me/april-tag', {}));

    return playerSchema.decode(response);
  }

  /** The player analog of {@link downloadAprilTag}, for the signed-in player user's own entry. */
  async downloadMyAprilTag(): Promise<Blob> {
    return await firstValueFrom(
      this.http.get('/players/me/april-tag', {
        responseType: 'blob',
      }),
    );
  }

  async updatePlayers(payload: UpdatePlayersPayload): Promise<Player[]> {
    const dto = updatePlayersPayloadSchema.encode(payload);
    const response = await firstValueFrom(this.http.put<PlayerDTO[]>('/players', dto));

    return response.map((playerDto) => playerSchema.decode(playerDto));
  }

  async updatePlayerImage(playerId: string, payload: UpdateImagePayload): Promise<ImageUpload> {
    const response = await firstValueFrom(
      this.http.put<ImageUpload>(`/players/${playerId}/image`, payload),
    );

    return response;
  }
}
